import React from 'react';
import { Table, Button, Badge, Avatar } from '@/components/ui';
import type { ProjectZone } from '../types/zones.types';
import { AuthButton } from '@/core/permissions/AuthButton';

interface ZonesDesktopTableProps {
  zones?: any[];
  data?: any[];
  isLoading?: boolean;
  onRowClick?: (zone: any) => void | Promise<void>;
  onEdit?: (zone: any) => void;
  onDelete?: (zone: any) => void;
}

const statusColors: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  active: 'bg-emerald-100 text-emerald-600',
  maintenance: 'bg-blue-100 text-blue-600',
  inactive: 'bg-amber-100 text-amber-600',
  archived: 'bg-slate-200 text-slate-500',
};

const statusLabels: Record<string, string> = {
  draft: 'Bản thảo',
  active: 'Hoạt động',
  maintenance: 'Bảo trì',
  inactive: 'Ngưng',
  archived: 'Lưu trữ',
};

const typeIcons: Record<string, string> = {
  lawn: 'ri-landscape-line',
  tree: 'ri-tree-line',
  shrub: 'ri-leaf-line',
  mixed: 'ri-stack-line',
  water: 'ri-drop-line',
  lighting: 'ri-lightbulb-line',
  infrastructure: 'ri-building-line',
  other: 'ri-more-line',
};

export function ZonesDesktopTable({ zones, data, onRowClick, onEdit, onDelete }: ZonesDesktopTableProps) {
  const zoneItems = zones ?? data ?? [];
  const columns = [
    {
      key: 'zone_code',
      title: 'Mã & Tên khu vực',
      render: (code: string, item: ProjectZone) => (
        <div className="flex flex-col">
          <span className="text-[10px] font-bold text-slate-400  tracking-wider">{code}</span>
          <span
            className="font-bold text-slate-900 hover:text-os-primary cursor-pointer transition-colors"
            onClick={() => onRowClick ? onRowClick(item) : window.location.href = `/maintenance/zones/detail/${item.id}`}
          >
            {item.zone_name}
          </span>
        </div>
      )
    },
    {
      key: 'zone_type',
      title: 'Phân loại',
      render: (type: string) => (
        <div className="flex items-center gap-2">
          <i className={`${typeIcons[type] || 'ri-checkbox-blank-circle-line'} text-slate-400`} />
          <span className="text-sm font-medium text-slate-700 capitalize">{type}</span>
        </div>
      )
    },
    {
      key: 'area_m2',
      title: 'Diện tích',
      render: (area: number) => (
        <div className="flex items-center gap-1">
          <span className="text-sm font-bold text-slate-900">{area?.toLocaleString() || '-'}</span>
          <span className="text-[10px] text-slate-400">m²</span>
        </div>
      )
    },
    {
      key: 'manager',
      title: 'Phụ trách (Foreman)',
      render: (manager: any) => (
        <div className="flex items-center gap-2">
          <Avatar
            src={manager?.avatar_url || undefined}
            fallback={manager?.full_name?.substring(0, 2).toUpperCase() || 'FM'}
            size="xs"
          />
          <span className="text-sm font-medium text-slate-700">{manager?.full_name || 'Chưa gán'}</span>
        </div>
      )
    },
    {
      key: 'status_code',
      title: 'Trạng thái',
      render: (status: string) => (
        <Badge className={`${statusColors[status] || 'bg-slate-100 text-slate-600'} border-transparent font-bold text-[10px]`}>
          {statusLabels[status] || status}
        </Badge>
      )
    },
    {
      key: 'actions',
      title: '',
      render: (_: any, item: ProjectZone) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" icon="ri-eye-line" onClick={() => window.location.href = `/maintenance/zones/detail/${item.id}`} />
          <AuthButton
            module="maintenance"
            action="update"
            variant="ghost"
            size="sm"
            icon="ri-edit-line"
            onClick={() => onEdit?.(item)}
          />
          <AuthButton
            module="maintenance"
            action="delete"
            variant="ghost"
            size="sm"
            icon="ri-delete-bin-line"
            className="text-red-500 hover:bg-red-50"
            onClick={() => onDelete?.(item)}
          />
        </div>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      data={zoneItems}
      className="bg-white rounded-xl shadow-sm border-none"
    />
  );
}
